import React, { useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
    Link as MuiLink,
    InputAdornment,
} from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import api from '../axios/api';
import {showNotification} from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';

// Enhanced Reset Password Schema with matching validation and password requirements
const ResetPasswordSchema = yup.object().shape({
    password: yup
        .string()
        .required("Password is required")
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            "Password must include uppercase, lowercase, number, and special character"
        ),
    confirmpassword: yup
        .string()
        .required("Confirm Password is required")
        .oneOf([yup.ref('password')], "Passwords must match"),
});

const ResetPassword = () => {
    const [showPassword, setShowPassword] = useState(false);
    const { token } = useParams();
    const dispatch=useDispatch();
    
    const form = useForm({
        resolver: yupResolver(ResetPasswordSchema),
        defaultValues: {
            password: '',
            confirmpassword: '',
            token: token,
        },
    });

    const { register, handleSubmit, formState: { errors } } = form;
    const navigate = useNavigate();

    const onSubmit = async (data) => {
        try {
            const payload = {
                password: data.password,
                token: token
            };

            // Make the API call to reset password
            const response = await api.post('/auth/resetpassword', payload);
            
            if (response.status === 200) {
                dispatch(
                    showNotification({
                        message: 'Your Password has been reset successfully',
                      type: "success",
                    })
                  );
                navigate('/login');
            } else {
                dispatch(
                    showNotification({
                        message: response.data.message || 'Failed to reset password',
                      type: "error",
                    })
                  );
            }
        } catch (error) {
            const errorMessage = error instanceof Error
                ? error.message
                : typeof error === 'string'
                    ? error
                    : 'An unknown error occurred';
            dispatch(
                showNotification({
                    message: errorMessage,
                  type: "error",
                })
              );
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f5f5f5',
                padding: 2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: { xs: '90%', md: '80%', lg: '70%' },
                    backgroundColor: 'white',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 3,
                }}
            >
                {/* Image Section */}
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        padding: 2,
                    }}
                >
                    <img
                        src="/vectors/Lockkey.jpg"
                        alt="Login Vector"
                        style={{
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                        }}
                    />
                </Box>

                {/* Form Section */}
                <Card
                    sx={{
                        flex: 1,
                        padding: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                >
                    <CardContent>
                        <Typography variant="h4" component="h4" gutterBottom>
                            Reset Your Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Enter a new password that includes uppercase, lowercase, number, and special character
                        </Typography>

                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    fullWidth
                                    label={
                                        <span>
                                          Password
                                          <span style={{ color: 'red' }}> *</span>
                                        </span>
                                      }
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="********"
                                    error={!!errors.password}
                                    helperText={errors.password?.message}
                                    {...register('password')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <EyeOff /> : <Eye />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    label={
                                        <span>
                                         Confirm Password
                                          <span style={{ color: 'red' }}> *</span>
                                        </span>
                                      }
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="********"
                                    error={!!errors.confirmpassword}
                                    helperText={errors.confirmpassword?.message}
                                    {...register('confirmpassword')}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <EyeOff /> : <Eye />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    sx={{ mt: 2 }}
                                >
                                    Reset password
                                </Button>
                            </Box>
                        </form>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default ResetPassword;